import { createContext, useContext, useEffect, useState } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as fbSignOut,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';

/* Administrator identity.
 
   Sign-in is Firebase Auth email and password. Being signed in is not enough:
   an account must also have a document at admins/{uid}, which is what the
   Firestore and Storage rules check before allowing any write.
 
   The client-side check here is for the interface alone. It decides what an
   administrator sees, never what they are permitted to do. Enforcement lives
   in the rules, where it cannot be bypassed by editing JavaScript. */

const AdminAuthContext = createContext({ user: null, isAdmin: false, loading: true });

export function AdminAuthProvider({ children }) {
  const [state, setState] = useState({ user: null, isAdmin: false, loading: true });

  useEffect(() => {
    if (!auth) {
      setState({ user: null, isAdmin: false, loading: false, unavailable: true });
      return undefined;
    }
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setState({ user: null, isAdmin: false, loading: false });
        return;
      }
      let isAdmin = false;
      try {
        const snap = await getDoc(doc(db, 'admins', user.uid));
        isAdmin = snap.exists();
      } catch {
        isAdmin = false;
      }
      setState({ user, isAdmin, loading: false });
    });
    return unsubscribe;
  }, []);

  return <AdminAuthContext.Provider value={state}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth() {
  return useContext(AdminAuthContext);
}

export function signIn(email, password) {
  if (!auth) return Promise.reject(new Error('auth-unavailable'));
  return signInWithEmailAndPassword(auth, email, password);
}

export function signOut() {
  if (!auth) return Promise.resolve();
  return fbSignOut(auth);
}

export function resetPassword(email) {
  if (!auth) return Promise.reject(new Error('auth-unavailable'));
  return sendPasswordResetEmail(auth, email);
}

export function friendlyAuthError(error) {
  if (error && error.message === 'auth-unavailable') {
    return 'Sign-in is unavailable: the site\u2019s Firebase keys are missing from this build.';
  }
  const code = (error && error.code) || '';
  if (code.includes('invalid-credential') || code.includes('wrong-password') || code.includes('user-not-found')) {
    return 'That email and password combination was not recognized.';
  }
  if (code.includes('too-many-requests')) return 'Too many attempts. Wait a few minutes and try again.';
  if (code.includes('invalid-email')) return 'That does not look like a valid email address.';
  if (code.includes('network')) return 'Network problem. Check your connection and try again.';
  return 'Sign-in failed. Please try again.';
}
